import { DateOnly } from '@if/core/date'
import { EPaymentPlan } from '@if/core/types'
import { useComputeMonthlyTerms } from '@src/data/api/computation/computation-api'
import type { ComputeMonthTermFrequencyDto } from '@src/data/types/ComputeMonthTermFrequencyDto'
import type { Merchant } from '@src/data/types/Merchant'
import { formatPaymentPlanForList } from '@src/services/Formatter'
import { useMemo } from 'react'

/**
 * A calculator quote has no credit decision behind it, so there is no real payment cap —
 * this is the sanity ceiling the merchant dashboard's calculator uses. Passing 0 would make
 * the terms service reject every term (`payment > MaxPmtAmount`).
 */
const MAX_PAYMENT_AMOUNT = 5000

/** 84-month terms only open up above this amount, same threshold as the worksheet. */
const LARGE_LOAN_AMOUNT = 10_000

/** The calculator never quotes below a year. */
const MIN_TERM = 12

/**
 * The default (non-promotional) plan sentinel. Widened to `string` because plan ids are plain
 * strings once a merchant's own plans are in the list — the enum only names this one value.
 */
export const REGULAR_PLAN_ID: string = EPaymentPlan.RegularDailyInterests

export interface CalculatorOption<TValue> {
  value: TValue
  label: string
}

/**
 * Regular daily interest plus the merchant's plans that are active today — the same set the
 * merchant dashboard's calculator offers, so both quote from the same plan list.
 */
export const usePaymentPlanOptions = (merchant: Merchant | null): CalculatorOption<string>[] =>
  useMemo(() => {
    const now = new Date()
    const options: CalculatorOption<string>[] = [{ value: REGULAR_PLAN_ID, label: REGULAR_PLAN_ID }]

    for (const plan of merchant?.paymentPlans ?? []) {
      const activeFrom = new Date(plan.activeFrom)
      const expiresOn = plan.expiresOn ? new Date(plan.expiresOn) : null
      const isActive = activeFrom <= now && (expiresOn === null || expiresOn > now)
      if (isActive) options.push({ value: plan.id, label: formatPaymentPlanForList(plan) })
    }

    return options
  }, [merchant?.paymentPlans])

interface UseTermOptionsArgs {
  financingProgramId: string
  merchantId: string | null
  merchant: Merchant | null
  paymentPlanId: string
  amountRequested: number | null
  provinceIso: string
  includeProtection: boolean
  /**
   * The rate the last estimate was priced at. The terms service needs a rate to judge which
   * terms stay legal, and the rate is resolved server-side from the ERS — so the term list
   * only becomes available once an estimate has come back.
   */
  interestRate: number | null
  /** Frequency and both dates move the payment, so they move which terms stay legal. */
  paymentFrequency: string
  activationDate: DateOnly
  firstPaymentDate: DateOnly | null
}

/**
 * The terms the amount and plan allow. A promotional plan carries its own term, so that case
 * is answered from the plan itself; regular daily interest asks the terms service.
 */
export const useTermOptions = ({
  financingProgramId,
  merchantId,
  merchant,
  paymentPlanId,
  amountRequested,
  provinceIso,
  includeProtection,
  interestRate,
  paymentFrequency,
  activationDate,
  firstPaymentDate,
}: UseTermOptionsArgs): { options: CalculatorOption<number>[]; isLoading: boolean } => {
  const isPromoPlan = paymentPlanId !== REGULAR_PLAN_ID

  const dto = useMemo<ComputeMonthTermFrequencyDto | undefined>(() => {
    if (isPromoPlan || !merchantId || !provinceIso || !amountRequested || !interestRate) return undefined
    if (!firstPaymentDate) return undefined

    return {
      creditApplicationId: null,
      merchantId,
      paymentPlanId,
      interestRate,
      hasCoapplicant: false,
      stateIso: provinceIso,
      paymentFrequency,
      deliveryOn: activationDate,
      firstPaymentOn: firstPaymentDate,
      maxPmtAmount: MAX_PAYMENT_AMOUNT,
      amountRequested,
      includeInsurance: includeProtection,
      include84Months: amountRequested >= LARGE_LOAN_AMOUNT,
    }
  }, [
    isPromoPlan,
    merchantId,
    provinceIso,
    amountRequested,
    interestRate,
    paymentPlanId,
    includeProtection,
    paymentFrequency,
    activationDate,
    firstPaymentDate,
  ])

  const [monthlyTerms, isFetching] = useComputeMonthlyTerms(financingProgramId, dto)

  const options = useMemo<CalculatorOption<number>[]>(() => {
    if (isPromoPlan) {
      const planTerms = [
        ...new Set(
          (merchant?.paymentPlans ?? [])
            .filter((plan) => String(plan.id) === paymentPlanId && plan.loanTerm > 0)
            .map((plan) => plan.loanTerm),
        ),
      ].sort((a, b) => a - b)
      return planTerms.map((term) => ({ value: term, label: String(term) }))
    }

    return (monthlyTerms?.monthlyTerms ?? [])
      .filter((item) => item.term >= MIN_TERM)
      .map((item) => ({ value: item.term, label: String(item.term) }))
  }, [isPromoPlan, merchant?.paymentPlans, paymentPlanId, monthlyTerms])

  // A promotional plan answers from data already in hand, so only the regular path can be
  // mid-flight. `dto` being undefined means the request has not been sent yet — which for the
  // regular path is still "not answered".
  const isLoading = !isPromoPlan && (isFetching || (dto !== undefined && monthlyTerms === null))

  return { options, isLoading }
}
